import React, { useState, useEffect, useRef, useContext } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AppStateContext } from '../../../utils/contexts'
import { ekincareLogo, redCircle } from '../../../assets/images';
import moment from 'moment';
import { Tooltip } from 'antd';
import { companyMailerApi } from '../../../services';
import { LAUNCH_MAILER_RELEASE_DATE } from '../../../constants/companyMailer';

const SidebarGlyph = ({ name }) => {
  const glyphs = {
    overview: (
      <>
        <path d='M4 12.5 12 5l8 7.5' />
        <path d='M6.5 10.5V20h11V10.5' />
      </>
    ),
    preEmployment: (
      <>
        <path d='M7 4.5h10' />
        <path d='M9 3v3' />
        <path d='M15 3v3' />
        <path d='M5.5 8.5h13v10.5H5.5z' />
        <path d='M9 13h6' />
      </>
    ),
    utilization: (
      <>
        <path d='M5 19V9' />
        <path d='M12 19V5' />
        <path d='M19 19v-7' />
      </>
    ),
    engagement: (
      <>
        <path d='M4.5 6.5A2.5 2.5 0 0 1 7 4h10a2.5 2.5 0 0 1 2.5 2.5v5A2.5 2.5 0 0 1 17 14H12l-4 4v-4H7A2.5 2.5 0 0 1 4.5 11.5z' />
        <path d='M8 8h8' />
        <path d='M8 11h5' />
      </>
    ),
    analytics: (
      <>
        <path d='M5 19V6' />
        <path d='M11 19v-9' />
        <path d='M17 19v-5' />
        <path d='M4 19h16' />
      </>
    ),
    manage: (
      <>
        <path d='M8 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z' />
        <path d='M16 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z' />
        <path d='M3.5 20a4.5 4.5 0 0 1 9 0' />
        <path d='M13.5 20a3.5 3.5 0 0 1 7 0' />
      </>
    ),
    invoices: (
      <>
        <path d='M7 4h10v16l-2-1.5L12 20l-3-1.5L7 20V4Z' />
        <path d='M9.5 9h5' />
        <path d='M9.5 13h5' />
      </>
    ),
    network: (
      <>
        <path d='M12 4v4' />
        <path d='M6 9.5 3.5 14 6 18.5h5L13.5 14 11 9.5Z' />
        <path d='M18 9.5 15.5 14 18 18.5h5L25.5 14 23 9.5Z' transform='translate(-4 0)' />
      </>
    ),
    prelaunch: (
      <>
        <path d='M12 3v6' />
        <path d='m16.5 7.5-9 9' />
        <path d='M12 21a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z' />
      </>
    ),
  };

  return (
    <span className='shrink-0 text-gray-500 transition-colors duration-150 group-hover:text-gray-700'>
      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' className='h-4 w-4'>
        {glyphs[name] || glyphs.overview}
      </svg>
    </span>
  );
};

const SideBarPill = ({ to, text, icon }) => {
  return (
    <div>
      <NavLink
        to={'/' + to}
        activeClassName='border-blue-700 bg-blue-100 text-blue-700 font-fig-semibold'
        className='group flex w-full items-center gap-2 border-l-2 border-transparent px-5 py-2 text-xs leading-5 text-gray-700 no-underline transition-all duration-150 hover:bg-gray-200 hover:text-gray-900'
      >
        <SidebarGlyph name={icon} />
        <span>{text}</span>
      </NavLink>
    </div>
  );
};

const DropDownItem = ({ to, text }) => {
  if (text) {
    return (
      <NavLink
        to={to}
        activeClassName='border-blue-700 bg-blue-100 text-blue-700 font-fig-semibold'
        className='group flex w-full items-center border-l-2 border-transparent py-2 pl-14 pr-5 text-xs text-gray-700 no-underline transition-all duration-150 hover:bg-gray-200 hover:text-gray-900'
      >
        {text}
      </NavLink>
    );
  } else {
    return (false);
  }
};

const DropDownMenu = ({ title, list, icon }) => {
  const location = useLocation()
  const pathsList = useRef(list.map(item => item.to))
  const [showItems, setshowItems] = useState(pathsList.current.includes(location.pathname))

  useEffect(() => {
    setshowItems(pathsList.current.includes(location.pathname))
  }, [location.pathname])

  return (
    <div className='cursor-pointer'>
      <div
        className={`${showItems ? 'border-blue-700 bg-blue-1000 text-blue-700 font-fig-semibold' : 'border-transparent text-gray-700'} group flex w-full items-center gap-2 border-l-2 px-4 py-2 text-xs leading-5 no-underline transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:bg-gray-50`}
        aria-controls='sub-menu-1'
        aria-expanded='false'
        onClick={() => {
          setshowItems(flag => !flag);
        }}
      >
        <SidebarGlyph name={icon} />
        <span>{title}</span>
        <svg
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.8'
          strokeLinecap='round'
          strokeLinejoin='round'
          className={`ml-auto h-3 w-3 transform transition-transform duration-200 ${showItems ? 'rotate-90' : ''}`}
        >
          <path d='m9 6 6 6-6 6' />
        </svg>
      </div>
      {showItems && <div id='sub-menu-1'>{list.map(({ to, text, show = true }) => show && <DropDownItem key={typeof text === 'string' ? text : to} to={to} text={text} />)}</div>}
    </div>
  );
}

const SideBar = ({ children, previousState, benefitList, isBenefitListLoaded }) => {

  const OtherBenefitPaths = [
    { to: 'doctor-consultation', text: 'Consultation' },
    // { to: 'health-risk-assessment', text: 'Health Risk Assessment' },
    { to: 'pharmacy', text: 'Pharmacy' },
    { to: 'vaccination', text: 'Vaccination' },
    { to: 'gym-and-fitness', text: 'Gyms and Fitness' },
    { to: 'health-coach-programs', text: 'Health Coach Programs' },
  ];

  // Check if any benefit_name includes "vaccination"
  const hasVaccination = benefitList?.some(benefit =>
    benefit.benefit_name?.toLowerCase().includes('vaccination')
  );

  // Remove vaccination path if found in benefitList
  const updatedOtherBenefitPaths = hasVaccination
    ? OtherBenefitPaths.filter(path => path.to !== 'vaccination')
    : OtherBenefitPaths;

  const [mailSent, setMailSent] = useState({ sent: true, post: false, sent_at: null });
  const role = (previousState.role || '').toLowerCase().replace(/ /g, '_')
  const { showAddIcon, showFamilyIcon, showDeleteIcon } = useContext(AppStateContext)
  const mergedBenefitList = [...benefitList, ...updatedOtherBenefitPaths];
  let launch_date = sessionStorage.getItem('launch_date');
  let digitization = previousState.digitization;

  // Exclude 'Health checks' when digitization is not 'full' 
  let analyticsSubmenuList = [
    (digitization === 'digitization_disable' ? { to: '', text: '' } : { to: '/test-results', text: 'Health checks' }),
    { to: '/mental-wellness', text: 'Mental wellness ' },
    { to: '/health-risks', text: 'Health risks' },
    { to: '/demographics', text: 'Demographics' },
    { to: '/challenges', text: 'Challenges' },
    { to: '/steps', text: 'Steps' },
    (mailSent.post ? (mailSent.sent_at ? { to: '/mailer', text: 'Pre Launch' } : { to: '', text: '' }) : { to: '', text: '' }),
    { to: '/mis-pulse', text: 'Mis Pulse' }
  ];
  let is_active = sessionStorage.getItem('is_active')
  const isChildCompany = JSON.parse(sessionStorage.getItem('is_child_company'));
  useEffect(() => {
    if (is_active === 'inactive' && role === 'staff') {
      setMailSent((prev) => { return { ...prev, ...{ sent: false } } })
    }
    else {
      if (launch_date > LAUNCH_MAILER_RELEASE_DATE && ['staff', 'csm'].includes(role)) {
        companyMailerApi.post_pre_launch({},
          (res) => {
            let data = res.data
            if (moment(new Date()).format('YYYY-MM-DD') >= moment(data?.sent_at).add(30, 'days').format('YYYY-MM-DD')) {
              setMailSent((prev) => { return { ...prev, ...{ post: true, sent_at: data?.sent_at } } })
            }
          },
          (error) => {
            let launch_date = sessionStorage.getItem('launch_date')
            if (moment(new Date()).format('YYYY-MM-DD') > moment(launch_date).add(90, 'days').format('YYYY-MM-DD')) {
              setMailSent((prev) => { return { ...prev, ...{ sent: true, post: true, sent_at: null } } })
            } else {
              setMailSent((prev) => { return { ...prev, ...{ post: false } } })
            }
          }
        )
      }
      setMailSent((prev) => { return { ...prev, ...{ sent: true } } })
    }
  }, [])

  const companies = JSON.parse(sessionStorage.getItem('companies')) || [];
  const companyId = sessionStorage.getItem('company_id');
  const enablePpmc = companies.find(item => item.id == companyId)?.enable_ppmc;
  const isOPD = sessionStorage.getItem('isOPDAccount') === 'true';

  return (
    <div className='flex min-h-screen overflow-hidden bg-gray-50'>
      <div className='fixed inset-y-0 z-20 flex h-screen w-60 shrink-0 flex-col overflow-y-auto border-r border-gray-250 bg-gray-50 py-6'>
        <div className='border-b border-gray-250 px-5 pb-4'>
          <img src={ekincareLogo} alt='ekincare' className='h-6 w-auto' />
        </div>
        <div className='mt-5 flex flex-grow flex-col'>

          <nav className='flex-1 py-1' aria-label='Sidebar'>
            <div style={(LAUNCH_MAILER_RELEASE_DATE < launch_date ? (mailSent.post ? { display: 'none' } : { display: 'block' }) : { display: 'none' })}>
              {['staff', 'csm'].includes(role) && (
                <SideBarPill to='mailer' text='Pre Launch' icon='prelaunch' />
              )}
            </div>

            <Tooltip
              placement='bottomLeft'
              overlayStyle={{ maxWidth: '235px' }}
              color={'#0F1115'}
              key='pink'
              title={`${mailSent.sent ? '' : 'This feature will unlock after sending the official launch emailer'}`}
              align
            >
              <div>
                <div className={`${mailSent.sent ? '' : 'opacity-50 pointer-events-none'}`}>

                  {!['regional_staff', 'preemp', 'preemprequest'].includes(role) && (
                    <SideBarPill to={isOPD ? 'opd-overview' : 'overview'} text={isOPD ? 'OPD Overview' : 'Overview'} icon='overview' />
                  )}
                  {!['broker'].includes(role) && (
                    <SideBarPill to='pre-employee-checkup' text='Pre Employment' icon='preEmployment' />
                  )}
                  {!['broker'].includes(role) && enablePpmc && (
                    <SideBarPill to='ppmc-employee-checkup' text='Pre-Policy Medical Checkup' icon='preEmployment' />
                  )}
                  {['staff', 'sales', 'csm', 'csmmaster', 'broker'].includes(role) && (
                    <div>
                      {isBenefitListLoaded && (<DropDownMenu
                        title='Utilization' list={mergedBenefitList.map((benefit) => ({
                          to: benefit.benefit_type ? `/utilization/${benefit.benefit_type}` : `/${benefit.to}`,
                          text: benefit.benefit_name || benefit.text
                        }))}
                        icon='utilization' />)}
                    </div>
                  )}
                  {!['regional_staff', 'preemp', 'preemprequest', 'broker'].includes(role) && (
                    <DropDownMenu title='Engagement' list={[{ to: '/health-risk-assessment', text: 'Health Risk Assessment' }, { to: '/stepathon', text: 'Stepathon' }, { to: '/wellness-calendar', text: 'Calendar' }, { to: '/benefit-adoption-mailers', text: 'Benefit Mailers' }]} icon='engagement' />
                  )}
                  {!['regional_staff', 'preemp', 'preemprequest'].includes(role) && (
                    <DropDownMenu title='Analytics' list={analyticsSubmenuList} icon='analytics' />
                  )}
                </div>
              </div>

            </Tooltip>
            {!['regional_staff', 'broker', 'preemp', 'csm', 'csmmaster', 'preemprequest'].includes(role) && (
              <DropDownMenu
                title={<span className='flex flex-row font-semibold'>Manage Employees{(showAddIcon || showDeleteIcon || showFamilyIcon) && <img className='h-2 w-2 mt-1.5 ml-2 rounded-full' src={redCircle} />}</span>}
                list={[{ to: '/manage-employee', text: 'Employee list' },
                { to: '/employee-registration', text: <span className='flex flex-row font-semibold'>Add Employees{showAddIcon && <img className='h-2 w-2 mt-1.5 ml-2 rounded-full' src={redCircle} />}</span> },
                { to: '/deactivate-employees', text: <span className='flex flex-row font-semibold'>Deactivate Employees{showDeleteIcon && <img className='h-2 w-2 mt-1.5 ml-2 rounded-full' src={redCircle} />}</span> },
                { to: '/family-registration', text: <span className='flex flex-row font-semibold'>Family Registration{showFamilyIcon && <img className='h-2 w-2 mt-1.5 ml-2 rounded-full' src={redCircle} />}</span> }]
                }
                icon='manage'
              />
            )}
            <Tooltip
              placement='bottomLeft'
              overlayStyle={{ maxWidth: '235px' }}
              color='#0F1115'
              key='red'
              title={`${mailSent.sent ? '' : 'This feature will unlock after sending the official launch emailer'}`}
            >
              <div>
                <div className={`${mailSent.sent ? '' : 'opacity-50 pointer-events-none'}`}>
                  {['staff', 'csm', 'csmmaster', 'sales'].includes(role) && (
                    <SideBarPill to='invoices' text='MIS and Invoices' icon='invoices' />
                  )}
                </div>
              </div>
              {sessionStorage.getItem('logged_in_company_id') === '100' && <div>
                <div className={`${mailSent.sent ? '' : 'opacity-50 pointer-events-none'}`}>
                  <SideBarPill to='provider-network' text='Provider Network Coverage' icon='network' />
                </div>
              </div>}
            </Tooltip>
            {/* 
            <SideBarPill to='hrms_sync' text='Auto Enroll' svgCode={analytics_icon} /> */}

          </nav>
        </div>
      </div>

      <main
        className='relative ml-60 flex-1 overflow-y-auto bg-gray-50 focus:outline-none'
        tabIndex='0'
      >
        {children}
      </main>
    </div>
  );
};

export default SideBar
